import { useState, useEffect } from "react";
import {
  UserIcon,
  UserGroupIcon,
  EnvelopeIcon,
  PhoneIcon,
  AcademicCapIcon,
  PencilIcon,
  TrashIcon,
  PlusIcon,
  XMarkIcon,
  ArrowRightStartOnRectangleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CheckIcon,
  ArrowDownTrayIcon,
  Cog6ToothIcon,
  ClockIcon,
} from "@heroicons/react/24/outline";
import { useAuth } from "../contexts/AuthContext";
import { useStudioPlan } from "../hooks/useStudioPlan";
import Notification from "../components/Notification";
import StaffStatCard from "../components/staff/StaffStatCard";
import UpgradePrompt from "../components/UpgradePrompt";

// Security constants
const MAX_LENGTHS: Record<string, number> = {
  searchTerm: 100,
  name: 100,
  email: 254,
  phone: 20,
};

const SUBMISSION_COOLDOWN = 2000; // 2 seconds between submissions

// Input sanitization function
const sanitizeInput = (input: string, fieldType?: string): string => {
  if (typeof input !== 'string') return '';
  
  let sanitized = input;
  
  // For search terms and text fields, preserve spaces but remove dangerous content
  if (fieldType === 'searchTerm' || fieldType === 'name') {
    // Only remove dangerous patterns, preserve spaces
    sanitized = sanitized.replace(/[<>]/g, ''); // Remove HTML tags
    sanitized = sanitized.replace(/javascript:/gi, ''); // Remove javascript protocol
    sanitized = sanitized.replace(/vbscript:/gi, ''); // Remove vbscript protocol
    sanitized = sanitized.replace(/on\w+\s*=/gi, ''); // Remove event handlers
    sanitized = sanitized.replace(/data:text\/html/gi, ''); // Remove data URLs
    // Don't trim to preserve user intent
  } else {
    // For other fields (like email, phone), use more aggressive sanitization
    sanitized = sanitized.trim();
    sanitized = sanitized.replace(/[<>]/g, '');
    sanitized = sanitized.replace(/javascript:/gi, '');
    sanitized = sanitized.replace(/data:text\/html/gi, '');
    sanitized = sanitized.replace(/vbscript:/gi, '');
    sanitized = sanitized.replace(/on\w+\s*=/gi, '');
  }
  
  return sanitized;
};

// Enhanced validation function
const validateInput = (value: string, type: string): boolean => {
  if (!value || typeof value !== 'string') return false;
  
  const sanitized = sanitizeInput(value, type);
  if (sanitized.length === 0) return false;

  // Check length limits
  if (MAX_LENGTHS[type] && sanitized.length > MAX_LENGTHS[type]) {
    return false;
  }
  
  // Type-specific validation
  switch (type) {
    case 'searchTerm':
    case 'name':
      // Check for dangerous patterns in text fields
      const textDangerousPatterns = /<script\b|javascript:|vbscript:|on\w+\s*=|data:text\/html/i;
      return !textDangerousPatterns.test(sanitized) && sanitized.length >= 1;
    case 'email':
      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      return emailRegex.test(sanitized);
    case 'phone':
      const phoneRegex = /^[\d\s\-\(\)\.]+$/;
      return phoneRegex.test(sanitized) && sanitized.replace(/\D/g, '').length >= 10;
    default:
      return sanitized.length > 0;
  }
};

// Security event logging
const logSecurityEvent = (event: string, details: any) => {
  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.warn(`Staff Security Event: ${event}`, {
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      details
    });
  }
};



interface StaffRole {
  id: string;
  name: string;
  studio_id: string;
  created_at: string;
}

interface StaffMember {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  role: string;
  studio_id: string;
  created_at: string;
  hourly_rate: number;
  is_signed_in: boolean;
  staff_number?: number;
  role_name?: { id: string; name: string } | null;
}

interface AssignedClass {
  id: string;
  class_name: string;
  days: string[];
  start_time: string;
  end_time: string;
  type: string;
  age_range: string;
}

interface RoleAssignment {
  staff_role_id: string;
  hourly_rate: string;
  checked: boolean;
}

interface FormData {
  id?: string;
  name: string;
  email: string | null;
  phone: string | null;
  roleAssignments: RoleAssignment[];
}

const MONTH_NAMES = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December",
];

const WEEK_DAY_NAMES = ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"];
const WEEK_DAY_SHORT = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];

const getMonday = (date: Date): Date => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
};

const getWeekDays = (weekStart: Date): Date[] =>
  Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart);
    d.setDate(weekStart.getDate() + i);
    return d;
  });

const formatWeekRange = (weekStart: Date): string => {
  const end = new Date(weekStart);
  end.setDate(weekStart.getDate() + 6);
  const opts: Intl.DateTimeFormatOptions = { month: "short", day: "numeric" };
  return `${weekStart.toLocaleDateString("en-US", opts)} – ${end.toLocaleDateString("en-US", { ...opts, year: "numeric" })}`;
};

const formatScheduleTime = (t: string): string => {
  if (!t) return "";
  const [h, m] = t.split(":").map(Number);
  const period = h >= 12 ? "pm" : "am";
  const hour = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${hour}:${String(m).padStart(2, "0")}${period}`;
};

const CLASS_TYPE_COLORS: Record<string, { bg: string; text: string; dot: string }> = {
  Regular:  { bg: "bg-indigo-50 dark:bg-indigo-900/30",  text: "text-indigo-800 dark:text-indigo-200",  dot: "bg-indigo-500" },
  Private:  { bg: "bg-purple-50 dark:bg-purple-900/30",  text: "text-purple-800 dark:text-purple-200",  dot: "bg-purple-500" },
  Event:    { bg: "bg-pink-50 dark:bg-pink-900/30",      text: "text-pink-800 dark:text-pink-200",      dot: "bg-pink-500"   },
  Workshop: { bg: "bg-orange-50 dark:bg-orange-900/30",  text: "text-orange-800 dark:text-orange-200",  dot: "bg-orange-500" },
};
const getClassColors = (type: string) =>
  CLASS_TYPE_COLORS[type] ?? { bg: "bg-gray-100 dark:bg-gray-700", text: "text-gray-700 dark:text-gray-300", dot: "bg-gray-400" };

// Format decimal hours → "Xh Ym" (e.g. 2.083 → "2h 5m")
const formatHours = (decimal: number): string => {
  const totalMins = Math.round(decimal * 60);
  const h = Math.floor(totalMins / 60);
  const m = totalMins % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
};

// Precise version that shows seconds — used in session history rows
const formatHoursWithSeconds = (decimal: number): string => {
  const totalSecs = Math.round(decimal * 3600);
  const h = Math.floor(totalSecs / 3600);
  const m = Math.floor((totalSecs % 3600) / 60);
  const s = totalSecs % 60;
  if (h > 0) return s > 0 ? `${h}h ${m}m ${s}s` : `${h}h ${m}m`;
  if (m > 0) return s > 0 ? `${m}m ${s}s` : `${m}m`;
  return `${s}s`;
};

// Convert decimal hours → "HH:MM" for the inline editor
const decimalToHHMM = (decimal: number): string => {
  const totalMins = Math.round(decimal * 60);
  const h = Math.floor(totalMins / 60);
  const m = totalMins % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};

// HH:MM:SS version for CSV export — preserves seconds precision
const decimalToHHMMSS = (decimal: number): string => {
  const totalSecs = Math.round(decimal * 3600);
  const h = Math.floor(totalSecs / 3600);
  const m = Math.floor((totalSecs % 3600) / 60);
  const s = totalSecs % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
};

// Parse "HH:MM" back to decimal hours for the backend
const hhmmToDecimal = (value: string): number => {
  const [hPart, mPart] = value.split(":");
  const h = parseInt(hPart, 10) || 0;
  const m = parseInt(mPart, 10) || 0;
  return parseFloat((h + m / 60).toFixed(4));
};

// Bi-weekly helpers

/** Given an anchor date string (YYYY-MM-DD) and a reference Date, return the
 *  start/end Dates of the 14-day period that contains the reference date. */
const getBiweeklyPeriod = (anchorStr: string, ref: Date): { start: Date; end: Date } => {
  const anchor = new Date(anchorStr + "T00:00:00");
  const diffMs = ref.getTime() - anchor.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const periodNum = Math.floor(diffDays / 14);
  const start = new Date(anchor);
  start.setDate(start.getDate() + periodNum * 14);
  const end = new Date(start);
  end.setDate(end.getDate() + 13);
  return { start, end };
};

/** Format a Date as YYYY-MM-DD for API params (local calendar day — not UTC via toISOString). */
const toDateStr = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

/** Format a Date as "Mon D" (e.g. "Mar 1") */
const formatShortDate = (d: Date): string =>
  d.toLocaleDateString("en-US", { month: "short", day: "numeric" });

/** Format a bi-weekly period as "Mar 1 – Mar 14" */
const formatPeriodLabel = (start: Date, end: Date): string =>
  `${formatShortDate(start)} – ${formatShortDate(end)}`;

// Format "HH:MM:SS" or "HH:MM" → "H:MM AM/PM"
const formatTime = (t: string): string => {
  if (!t) return "";
  const [h, m] = t.split(":");
  const hour = parseInt(h, 10);
  if (isNaN(hour)) return t;
  const ampm = hour >= 12 ? "PM" : "AM";
  const display = hour % 12 === 0 ? 12 : hour % 12;
  return `${display}:${m} ${ampm}`;
};

export default function Staff() {
  const { user, getAuthToken } = useAuth();
  const { plan, hasCapability, loading: planLoading } = useStudioPlan();
  const canUseStaffPayroll = hasCapability("staffPayroll");
  const [searchTerm, setSearchTerm] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingStaff, setEditingStaff] = useState<StaffMember | null>(null);
  const [staffToDelete, setStaffToDelete] = useState<StaffMember | null>(null);
  const [staff, setStaff] = useState<StaffMember[]>([]);
  const staffLimit = plan?.capabilities.maxStaff ?? null;
  const staffLimitReached =
    staffLimit != null && staff.length >= staffLimit;
  const [staffRoles, setStaffRoles] = useState<StaffRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState<FormData>({
    name: "",
    email: "",
    phone: "",
    roleAssignments: [],
  });
  const [isLoadingAssignments, setIsLoadingAssignments] = useState(false);

  // Hours & pay state
  const now = new Date();
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);
  const [hoursMap, setHoursMap] = useState<Record<string, number>>({});
  const [payMap, setPayMap] = useState<Record<string, number>>({});
  const [classesForStaff, setClassesForStaff] = useState<Record<string, AssignedClass[]>>({});
  const [allClasses, setAllClasses] = useState<any[]>([]);
  const [allEvents, setAllEvents] = useState<any[]>([]);
  const [scheduleWeekStart, setScheduleWeekStart] = useState<Date>(() => getMonday(new Date()));
  const [editingHoursId, setEditingHoursId] = useState<string | null>(null);
  const [editingHoursValue, setEditingHoursValue] = useState<string>("");
  const [classesModalStaff, setClassesModalStaff] = useState<StaffMember | null>(null);
  const [sessionHistoryStaff, setSessionHistoryStaff] = useState<StaffMember | null>(null);
  const [sessionHistory, setSessionHistory] = useState<any[]>([]);
  const [isLoadingSessions, setIsLoadingSessions] = useState(false);
  const [editingSession, setEditingSession] = useState<{ id: string; field: "in" | "out"; value: string } | null>(null);
  const [isSavingSession, setIsSavingSession] = useState(false);
  // Period navigation inside the session history modal
  const [sessionModalYear, setSessionModalYear] = useState(now.getFullYear());
  const [sessionModalMonth, setSessionModalMonth] = useState(now.getMonth() + 1);
  const [sessionModalBwStart, setSessionModalBwStart] = useState<Date | null>(null);
  const [sessionFilterFrom, setSessionFilterFrom] = useState("");
  const [sessionFilterTo, setSessionFilterTo] = useState("");
  const [studioId, setStudioId] = useState<string | null>(null);
  const [notification, setNotification] = useState<{
    show: boolean;
    message: string;
    title?: string;
  }>({
    show: false,
    message: "",
  });

  // Export modal state
  const [showExportModal, setShowExportModal] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // Shift scheduler state
  const [shifts, setShifts] = useState<any[]>([]);
  const [isLoadingShifts, setIsLoadingShifts] = useState(false);
  const [shiftWeekStart, setShiftWeekStart] = useState<Date>(() => {
    // Start on Monday of the current week
    const d = new Date();
    const day = d.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    d.setDate(d.getDate() + diff);
    d.setHours(0, 0, 0, 0);
    return d;
  });
  const [showShiftModal, setShowShiftModal] = useState(false);
  const [editingShift, setEditingShift] = useState<any | null>(null);
  const [shiftForm, setShiftForm] = useState({
    staff_id: "",
    staff_role_id: "",
    shift_date: "",
    start_time: "",
    end_time: "",
    notes: "",
    is_recurring: false,
    recurrence_days: [] as string[],
    recurrence_end_date: "",
  });
  const [isSavingShift, setIsSavingShift] = useState(false);

  // Pay period state
  const [payPeriodType, setPayPeriodType] = useState<"monthly" | "biweekly">("monthly");
  const [payPeriodAnchor, setPayPeriodAnchor] = useState<string | null>(null);
  const [bwPeriodStart, setBwPeriodStart] = useState<Date | null>(null);
  const [showPayPeriodSettings, setShowPayPeriodSettings] = useState(false);
  const [settingsType, setSettingsType] = useState<"monthly" | "biweekly">("monthly");
  const [settingsAnchor, setSettingsAnchor] = useState("");

  // Security-related state
  const [csrfToken, setCsrfToken] = useState<string>('');
  const [lastSubmissionTime, setLastSubmissionTime] = useState<number>(0);

  const fetchCsrfToken = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/supabase/csrf-token`);
      if (response.ok) {
        const data = await response.json();
        if (data.token) {
          setCsrfToken(data.token);
        } else {
          logSecurityEvent('CSRF_TOKEN_INVALID_RESPONSE', { data });
        }
      } else {
        logSecurityEvent('CSRF_TOKEN_FETCH_ERROR', { status: response.status, statusText: response.statusText });
      }
    } catch (error) {
      logSecurityEvent('CSRF_TOKEN_FETCH_FAILED', { error: error instanceof Error ? error.message : 'Unknown error' });
      // In development, generate a fallback token
      if (process.env.NODE_ENV !== 'production') {
        const fallbackToken = Math.random().toString(36).substring(2) + Date.now().toString(36);
        setCsrfToken(fallbackToken);
        // eslint-disable-next-line no-console
        console.warn('Using fallback CSRF token in development mode');
      }
    }
  };

  const handleEditStaff = async (member: StaffMember) => {
    setEditingStaff(member);
    setIsModalOpen(true);
    setIsLoadingAssignments(true);
    // Seed with empty assignments while loading
    setFormData({
      name: member.name,
      email: member.email,
      phone: member.phone,
      roleAssignments: staffRoles.map((r) => ({ staff_role_id: r.id, hourly_rate: "", checked: false })),
    });
    try {
      const authToken = getAuthToken();
      const base = process.env.REACT_APP_BACKEND_URL + "/api/supabase";
      const res = await fetch(`${base}/staff-role-assignments/${studioId}/${member.id}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const existing: { staff_role_id: string; hourly_rate: number }[] = res.ok ? await res.json() : [];
      setFormData({
        name: member.name,
        email: member.email,
        phone: member.phone,
        roleAssignments: staffRoles.map((r) => {
          const match = existing.find((a) => a.staff_role_id === r.id);
          return {
            staff_role_id: r.id,
            hourly_rate: match ? String(match.hourly_rate) : "",
            checked: !!match,
          };
        }),
      });
    } finally {
      setIsLoadingAssignments(false);
    }
  };

  const handleSaveStaff = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const authToken = getAuthToken();
    if (!user || !studioId || !authToken) return;

    // Client-side rate limiting
    const now = Date.now();
    if (now - lastSubmissionTime < SUBMISSION_COOLDOWN) {
      setNotification({
        show: true,
        message: "Please wait before saving the staff member again",
        title: "Rate Limited",
      });
      return;
    }

    // CSRF token validation (strict in production, flexible in development)
    if (!csrfToken) {
      logSecurityEvent('CSRF_TOKEN_MISSING', { action: 'saveStaff' });
      if (process.env.NODE_ENV === 'production') {
        setNotification({
          show: true,
          message: "Security token missing. Please refresh the page and try again.",
          title: "Error",
        });
        return;
      } else {
        // In development, log the issue but allow submission to continue
        // eslint-disable-next-line no-console
        console.warn('CSRF token missing in development mode - proceeding with submission');
      }
    }

    // Validate required fields
    if (!formData.name || !validateInput(formData.name, 'name')) {
      logSecurityEvent('INVALID_INPUT_ATTEMPT', { field: 'name', value: formData.name });
      setNotification({
        show: true,
        message: "Please enter a valid name",
        title: "Validation Error",
      });
      return;
    }

    if (!formData.email || !validateInput(formData.email, 'email')) {
      logSecurityEvent('INVALID_INPUT_ATTEMPT', { field: 'email', value: formData.email });
      setNotification({
        show: true,
        message: "Please enter a valid email address",
        title: "Validation Error",
      });
      return;
    }

    if (formData.phone && !validateInput(formData.phone, 'phone')) {
      logSecurityEvent('INVALID_INPUT_ATTEMPT', { field: 'phone', value: formData.phone });
      setNotification({
        show: true,
        message: "Please enter a valid phone number",
        title: "Validation Error",
      });
      return;
    }

    setLastSubmissionTime(now);

    if (!editingStaff && staffLimitReached) {
      setNotification({
        show: true,
        message: `Your plan allows up to ${staffLimit} staff members. Upgrade to add more.`,
        title: "Staff Limit Reached",
      });
      return;
    }

    const checkedRoles = formData.roleAssignments.filter((a) => a.checked);

    try {
      const primaryRole = checkedRoles[0] ?? null;
      const staffPayload: Record<string, any> = {
        name: formData.name,
        email: formData.email || null,
        phone: formData.phone || null,
        studio_id: studioId,
        hourly_rate: primaryRole ? (parseFloat(primaryRole.hourly_rate) || 0) : 0,
      };
      if (primaryRole?.staff_role_id) {
        staffPayload.role = primaryRole.staff_role_id;
      }

      let response;
      const apiUrl = process.env.REACT_APP_BACKEND_URL + "/api/supabase";

      if (editingStaff) {
        // Update existing staff member
        response = await fetch(`${apiUrl}/staff/${editingStaff.id}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
            "X-CSRF-Token": csrfToken,
          },
          body: JSON.stringify(staffPayload),
        });
      } else {
        // Create new staff member
        response = await fetch(`${apiUrl}/staff`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
            "X-CSRF-Token": csrfToken,
          },
          body: JSON.stringify(staffPayload),
        });
      }

      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}));
        throw new Error(
          (errBody as { error?: string }).error || "Failed to save staff member"
        );
      }

      const result = await response.json();

      // Save all role assignments (replaces existing ones)
      const rolesRes = await fetch(`${apiUrl}/staff-role-assignments`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
          "X-CSRF-Token": csrfToken,
        },
        body: JSON.stringify({
          staff_id: result.id,
          studio_id: studioId,
          assignments: checkedRoles.map((a) => ({
            staff_role_id: a.staff_role_id,
            hourly_rate: a.hourly_rate,
          })),
        }),
      });
      if (!rolesRes.ok) {
        const errBody = await rolesRes.json().catch(() => ({}));
        throw new Error(
          (errBody as { error?: string; message?: string }).error ||
            (errBody as { message?: string }).message ||
            "Failed to save role assignments. The staff profile was saved; try saving roles again."
        );
      }

      if (editingStaff) {
        // Update staff in local state
        setStaff((prev) =>
          prev.map((member) => {
            if (member.id === editingStaff.id) {
              return {
                ...result,
                role_name:
                  staffRoles.find((role) => role.id === result.role) || null,
              };
            }
            return member;
          })
        );
      } else {
        // Refresh staff list
        const staffResponse = await fetch(`${apiUrl}/staff/${studioId}`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        if (!staffResponse.ok) {
          throw new Error("Failed to refresh staff list");
        }
        const staffData = await staffResponse.json();
        const mappedStaff = (Array.isArray(staffData) ? staffData : []).map(
          (member) => ({
            ...member,
            role_name:
              staffRoles.find((role) => role.id === member.role) || null,
          })
        );
        setStaff(mappedStaff);
      }

      handleModalClose();
      setNotification({
        show: true,
        message: editingStaff
          ? "Staff member updated successfully"
          : "Staff member added successfully",
        title: "Success",
      });
    } catch (error) {
      logSecurityEvent('SAVE_STAFF_ERROR', { 
        error: error instanceof Error ? error.message : 'Unknown error',
        staffName: formData.name,
        isEditing: !!editingStaff
      });
      setNotification({
        show: true,
        message:
          error instanceof Error ? error.message : "Failed to save staff member",
        title: "Error",
      });
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setEditingStaff(null);
    setFormData({
      name: "",
      email: "",
      phone: "",
      roleAssignments: staffRoles.map((r) => ({ staff_role_id: r.id, hourly_rate: "", checked: false })),
    });
  };

  // Phone number formatting function
  const formatPhoneNumber = (value: string): string => {
    const digits = value.replace(/\D/g, "");
    if (digits.length <= 3) {
      return digits;
    } else if (digits.length <= 6) {
      return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    } else if (digits.length <= 10) {
      return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
    } else {
      return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(
        6,
        10
      )}`;
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    let newValue = value;
    let fieldType = '';

    // Sanitize input based on field type
    if (name === "name") {
      fieldType = 'name';
      newValue = sanitizeInput(value, fieldType);
    } else if (name === "email") {
      fieldType = 'email';
      newValue = sanitizeInput(value, fieldType);
    } else if (name === "phone") {
      fieldType = 'phone';
      const sanitizedPhone = sanitizeInput(value, fieldType);
      newValue = formatPhoneNumber(sanitizedPhone);
    } else {
      newValue = sanitizeInput(value);
    }

    // Validate input length
    if (fieldType && MAX_LENGTHS[fieldType] && newValue.length > MAX_LENGTHS[fieldType]) {
      newValue = newValue.substring(0, MAX_LENGTHS[fieldType]);
    }

    setFormData((prev) => ({
      ...prev,
      [name]: newValue === "" ? "" : newValue,
    }));
  };

  const confirmDelete = async () => {
    if (!staffToDelete) return;

    // Client-side rate limiting
    const now = Date.now();
    if (now - lastSubmissionTime < SUBMISSION_COOLDOWN) {
      setNotification({
        show: true,
        message: "Please wait before deleting another staff member",
        title: "Rate Limited",
      });
      return;
    }

    // CSRF token validation (strict in production, flexible in development)
    if (!csrfToken) {
      logSecurityEvent('CSRF_TOKEN_MISSING', { action: 'deleteStaff' });
      if (process.env.NODE_ENV === 'production') {
        setNotification({
          show: true,
          message: "Security token missing. Please refresh the page and try again.",
          title: "Error",
        });
        return;
      } else {
        // In development, log the issue but allow submission to continue
        // eslint-disable-next-line no-console
        console.warn('CSRF token missing in development mode - proceeding with deletion');
      }
    }

    setLastSubmissionTime(now);

    const authToken = getAuthToken();
    if (!authToken) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/staff/${staffToDelete.id}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${authToken}`,
            "X-CSRF-Token": csrfToken,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to delete staff member");
      }

      setStaff((prev) =>
        prev.filter((member) => member.id !== staffToDelete.id)
      );
      setStaffToDelete(null);
      setNotification({
        show: true,
        message: "Staff member deleted successfully",
        title: "Success",
      });
    } catch (error) {
      logSecurityEvent('DELETE_STAFF_ERROR', { 
        error: error instanceof Error ? error.message : 'Unknown error',
        staffId: staffToDelete?.id,
        staffName: staffToDelete?.name
      });
      setNotification({
        show: true,
        message: "Failed to delete staff member",
        title: "Error",
      });
    }
  };

  const cancelDelete = () => {
    setStaffToDelete(null);
  };

  // Handler for search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const sanitizedValue = sanitizeInput(e.target.value, 'searchTerm');
    
    // Allow empty search terms (for clearing the search)
    if (sanitizedValue === '') {
      setSearchTerm(sanitizedValue);
      return;
    }
    
    // Enhanced validation only for non-empty values
    if (!validateInput(sanitizedValue, 'searchTerm')) {
      logSecurityEvent('INVALID_INPUT_ATTEMPT', { field: 'searchTerm', value: sanitizedValue.substring(0, 50) + '...' });
      setNotification({
        show: true,
        message: "Invalid search term. Please check your input and try again.",
        title: "Validation Error",
      });
      return;
    }

    // Check length limits
    if (sanitizedValue.length > MAX_LENGTHS.searchTerm) {
      setNotification({
        show: true,
        message: `Search term too long. Maximum ${MAX_LENGTHS.searchTerm} characters.`,
        title: "Validation Error",
      });
      return;
    }

    setSearchTerm(sanitizedValue);
  };

  const filteredStaff = staff.filter((member) =>
    member.name.toLowerCase().includes(searchTerm.toLowerCase())
  );
  // Fetch studioId from backend
  useEffect(() => {
    const authToken = getAuthToken();
    if (!user || !authToken) return;
    
    // Fetch CSRF token first
    fetchCsrfToken();
    
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/supabase/studios/me`, {
      headers: { Authorization: `Bearer ${authToken}` },
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (data?.id) setStudioId(data.id);
      })
      .catch(() => {});
  }, [user, getAuthToken]);

  // Fetch pay period settings once studioId is known
  useEffect(() => {
    if (!canUseStaffPayroll) return;
    const authToken = getAuthToken();
    if (!studioId || !authToken) return;
    const base = process.env.REACT_APP_BACKEND_URL + "/api/supabase";
    fetch(`${base}/studios/${studioId}/pay-period`, {
      headers: { Authorization: `Bearer ${authToken}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!data) return;
        const type = data.pay_period_type === "biweekly" ? "biweekly" : "monthly";
        setPayPeriodType(type);
        setSettingsType(type);
        if (data.pay_period_anchor) {
          setPayPeriodAnchor(data.pay_period_anchor);
          setSettingsAnchor(data.pay_period_anchor);
          if (type === "biweekly") {
            const { start } = getBiweeklyPeriod(data.pay_period_anchor, now);
            setBwPeriodStart(start);
          }
        }
      })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studioId, canUseStaffPayroll]);

  // Fetch staff, belts, and staff roles from backend
  useEffect(() => {
    const authToken = getAuthToken();
    if (!studioId || !authToken) return;
    setLoading(true);
    
    const fetchData = async () => {
      try {
        const base = process.env.REACT_APP_BACKEND_URL + "/api/supabase";
        const [staffResponse, staffRolesResponse, classesResponse, eventsResponse] = await Promise.all([
          fetch(`${base}/staff/${studioId}`, { headers: { Authorization: `Bearer ${authToken}` } }),
          fetch(`${base}/staff-roles/${studioId}`, { headers: { Authorization: `Bearer ${authToken}` } }),
          fetch(`${base}/classes/${studioId}`, { headers: { Authorization: `Bearer ${authToken}` } }),
          fetch(`${base}/events/${studioId}`, { headers: { Authorization: `Bearer ${authToken}` } }),
        ]);

        if (!staffResponse.ok || !staffRolesResponse.ok) {
          throw new Error(`API request failed: Staff: ${staffResponse.status}, Roles: ${staffRolesResponse.status}`);
        }

        const [staffData, staffRolesData, classesData, eventsData] = await Promise.all([
          staffResponse.json(),
          staffRolesResponse.json(),
          classesResponse.ok ? classesResponse.json() : Promise.resolve([]),
          eventsResponse.ok ? eventsResponse.json() : Promise.resolve([]),
        ]);
        setAllEvents(Array.isArray(eventsData) ? eventsData : []);

        setStaffRoles(Array.isArray(staffRolesData) ? staffRolesData : []);

        const mappedStaff = (Array.isArray(staffData) ? staffData : []).map((member) => ({
          ...member,
          role_name: (Array.isArray(staffRolesData) ? staffRolesData : []).find(
            (role: StaffRole) => role.id === member.role
          ) || null,
        }));
        setStaff(mappedStaff);

        // Build class assignments map: staffId -> [{id, class_name, days, start_time, end_time, type, age_range}]
        // assigned_instructors in the DB is an array of staff ID strings
        const cMap: Record<string, AssignedClass[]> = {};
        (Array.isArray(classesData) ? classesData : []).forEach((cls: any) => {
          (Array.isArray(cls.assigned_instructors) ? cls.assigned_instructors : []).forEach(
            (staffId: string) => {
              if (!staffId) return;
              if (!cMap[staffId]) cMap[staffId] = [];
              cMap[staffId].push({
                id: cls.id,
                class_name: cls.class_name,
                days: Array.isArray(cls.days) ? cls.days : [],
                start_time: cls.start_time || "",
                end_time: cls.end_time || "",
                type: cls.type || "",
                age_range: cls.age_range || "",
              });
            }
          );
        });
        setClassesForStaff(cMap);
        setAllClasses(Array.isArray(classesData) ? classesData : []);
      } catch (error) {
        console.error("Error loading staff data:", error);
        setNotification({
          show: true,
          title: "Error",
          message: "Failed to load staff data. Please try again.",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [studioId, getAuthToken]);

  // Fetch hours whenever period/mode changes
  useEffect(() => {
    if (!canUseStaffPayroll) return;
    const authToken = getAuthToken();
    if (!studioId || !authToken) return;
    const base = process.env.REACT_APP_BACKEND_URL + "/api/supabase";

    let url: string;
    if (payPeriodType === "biweekly" && payPeriodAnchor && bwPeriodStart) {
      const { start, end } = getBiweeklyPeriod(payPeriodAnchor, bwPeriodStart);
      url = `${base}/staff-sessions/${studioId}?from=${toDateStr(start)}&to=${toDateStr(end)}`;
    } else {
      url = `${base}/staff-hours/${studioId}?year=${selectedYear}&month=${selectedMonth}`;
    }

    fetch(url, { headers: { Authorization: `Bearer ${authToken}` } })
      .then((r) => (r.ok ? r.json() : []))
      .then((data: { staff_id: string; hours_logged: number; pay_earned: number }[]) => {
        const hMap: Record<string, number> = {};
        const pMap: Record<string, number> = {};
        (Array.isArray(data) ? data : []).forEach((e) => {
          hMap[e.staff_id] = e.hours_logged;
          pMap[e.staff_id] = e.pay_earned ?? 0;
        });
        setHoursMap(hMap);
        setPayMap(pMap);
      })
      .catch((err) => {
        console.error("[Staff] Failed to load hours:", err);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studioId, selectedYear, selectedMonth, payPeriodType, payPeriodAnchor, bwPeriodStart, canUseStaffPayroll]);

  // Fetch shifts on mount and when week changes
  useEffect(() => {
    if (studioId) fetchShifts(shiftWeekStart);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studioId]);

  // Month navigation helpers
  const canGoBack = () => {
    const limit = new Date(now.getFullYear(), now.getMonth() - 11, 1);
    return new Date(selectedYear, selectedMonth - 1, 1) > limit;
  };
  const canGoForward = () =>
    !(selectedYear === now.getFullYear() && selectedMonth === now.getMonth() + 1);
  const goBack = () => {
    if (selectedMonth === 1) { setSelectedMonth(12); setSelectedYear((y) => y - 1); }
    else setSelectedMonth((m) => m - 1);
  };
  const goForward = () => {
    if (selectedMonth === 12) { setSelectedMonth(1); setSelectedYear((y) => y + 1); }
    else setSelectedMonth((m) => m + 1);
  };

  // Bi-weekly navigation helpers
  const goBwBack = () => {
    setBwPeriodStart((d) => {
      if (!d) return d;
      const next = new Date(d);
      next.setDate(next.getDate() - 14);
      return next;
    });
  };
  const goBwForward = () => {
    setBwPeriodStart((d) => {
      if (!d) return d;
      const next = new Date(d);
      next.setDate(next.getDate() + 14);
      return next;
    });
  };
  const canGoBwForward = (): boolean => {
    if (!bwPeriodStart || !payPeriodAnchor) return false;
    const { start: currentStart } = getBiweeklyPeriod(payPeriodAnchor, now);
    return bwPeriodStart.getTime() < currentStart.getTime();
  };
  const canGoBwBack = (): boolean => {
    if (!bwPeriodStart || !payPeriodAnchor) return false;
    // Allow going back up to ~6 months (13 periods)
    const limit = new Date(now);
    limit.setDate(limit.getDate() - 14 * 13);
    return bwPeriodStart > limit;
  };

  const activeBwPeriod =
    payPeriodType === "biweekly" && payPeriodAnchor && bwPeriodStart
      ? getBiweeklyPeriod(payPeriodAnchor, bwPeriodStart)
      : null;

  // Save pay period settings
  const savePayPeriodSettings = async () => {
    const authToken = getAuthToken();
    if (!authToken || !studioId) return;
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/studios/${studioId}/pay-period`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
          body: JSON.stringify({
            pay_period_type: settingsType,
            pay_period_anchor: settingsType === "biweekly" ? settingsAnchor : null,
          }),
        }
      );
      if (!res.ok) throw new Error();
      const saved = await res.json();
      const type = saved.pay_period_type === "biweekly" ? "biweekly" : "monthly";
      setPayPeriodType(type);
      setPayPeriodAnchor(saved.pay_period_anchor || null);
      if (type === "biweekly" && saved.pay_period_anchor) {
        const { start } = getBiweeklyPeriod(saved.pay_period_anchor, now);
        setBwPeriodStart(start);
      } else {
        setBwPeriodStart(null);
      }
      setShowPayPeriodSettings(false);
    } catch {
      setNotification({ show: true, title: "Error", message: "Failed to save pay period settings." });
    }
  };

  // Convert ISO timestamp → "HH:MM" (24h, local) for <input type="time">
  const tsToTimeInput = (ts: string): string => {
    const d = new Date(ts);
    return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  };

  // Merge a new "HH:MM" local time back into an existing ISO timestamp
  const mergeTimeIntoTs = (ts: string, timeValue: string): string => {
    const d = new Date(ts);
    const [h, m, s = 0] = timeValue.split(":").map(Number);
    d.setHours(h, m, s, 0);
    return d.toISOString();
  };

  // Save an edited session time
  const saveSessionEdit = async (domValue?: string) => {
    if (!editingSession || !studioId || isSavingSession) return;
    const authToken = getAuthToken();
    if (!authToken) return;

    const session = sessionHistory.find(s => s.id === editingSession.id);
    if (!session) return;

    setIsSavingSession(true);
    try {
      // Use domValue (read directly from the input element) to avoid stale React state.
      // Fall back with || so an empty string (which some browsers emit) doesn't slip through.
      const value = (domValue || editingSession.value);
      if (!value) {
        setEditingSession(null);
        setIsSavingSession(false);
        return;
      }

      const newInTs  = editingSession.field === "in"  ? mergeTimeIntoTs(session.clocked_in_at,  value) : session.clocked_in_at;
      const newOutTs = editingSession.field === "out" ? mergeTimeIntoTs(session.clocked_out_at, value) : session.clocked_out_at;

      if (newOutTs && new Date(newOutTs) <= new Date(newInTs)) {
        setNotification({ show: true, title: "Invalid Time", message: "Clock-out must be after clock-in." });
        setEditingSession(null);
        setIsSavingSession(false);
        return;
      }

      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/staff-sessions/${editingSession.id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
          body: JSON.stringify({ clocked_in_at: newInTs, clocked_out_at: newOutTs }),
        }
      );
      if (res.ok) {
        const updated = await res.json();
        const authToken2 = getAuthToken();
        let sessionsForTotals: any[];
        if (authToken2 && sessionHistoryStaff) {
          const list = await fetchSessions(
            sessionHistoryStaff.id,
            authToken2,
            sessionModalYear,
            sessionModalMonth,
            sessionModalBwStart
          );
          sessionsForTotals =
            list ?? sessionHistory.map((s) => (s.id === updated.id ? updated : s));
        } else {
          sessionsForTotals = sessionHistory.map((s) =>
            s.id === updated.id ? updated : s
          );
          setSessionHistory(sessionsForTotals);
        }
        if (sessionHistoryStaff) {
          const hMap = { ...hoursMap };
          const pMap = { ...payMap };
          hMap[sessionHistoryStaff.id] = parseFloat(
            sessionsForTotals
              .reduce((acc, s) => acc + (s.hours_worked ?? 0), 0)
              .toFixed(4)
          );
          pMap[sessionHistoryStaff.id] = parseFloat(
            sessionsForTotals
              .reduce((acc, s) => acc + Number(s.pay_earned ?? 0), 0)
              .toFixed(2)
          );
          setHoursMap(hMap);
          setPayMap(pMap);
        }
      } else {
        const errBody = await res.json().catch(() => ({}));
        console.error('[saveSessionEdit] PUT failed:', res.status, errBody);
        setNotification({ show: true, title: "Error", message: errBody?.error || `Failed to save (${res.status})` });
      }
    } catch (err) {
      console.error('[saveSessionEdit] exception:', err);
      setNotification({ show: true, title: "Error", message: "Failed to save session edit." });
    }
    setIsSavingSession(false);
    setEditingSession(null);
  };

  // Fetch sessions for a given period and staff member
  // If customFrom/customTo are provided they override the period navigation
  const fetchSessions = async (
    staffId: string,
    authToken: string,
    year: number,
    month: number,
    bwStart: Date | null,
    customFrom = "",
    customTo = "",
  ): Promise<any[] | undefined> => {
    if (!studioId) return undefined;
    const base = `${process.env.REACT_APP_BACKEND_URL}/api/supabase`;
    let url: string;
    if (customFrom && customTo) {
      url = `${base}/staff-sessions/${studioId}/${staffId}?from=${customFrom}&to=${customTo}`;
    } else if (payPeriodType === "biweekly" && payPeriodAnchor && bwStart) {
      const { start, end } = getBiweeklyPeriod(payPeriodAnchor, bwStart);
      url = `${base}/staff-sessions/${studioId}/${staffId}?from=${toDateStr(start)}&to=${toDateStr(end)}`;
    } else {
      const lastDay = new Date(year, month, 0);
      url = `${base}/staff-sessions/${studioId}/${staffId}?from=${year}-${String(month).padStart(2,"0")}-01&to=${toDateStr(lastDay)}`;
    }
    try {
      const res = await fetch(url, { headers: { Authorization: `Bearer ${authToken}` } });
      const data = res.ok ? await res.json() : [];
      const list = Array.isArray(data) ? data : [];
      setSessionHistory(list);
      return list;
    } catch (_) {
      return undefined;
    }
  };

  // Open session history modal — initialise to the same period shown in the main table
  const openSessionHistory = async (member: StaffMember) => {
    const authToken = getAuthToken();
    if (!authToken || !studioId) return;

    // Mirror the current period from the main table
    const initYear  = selectedYear;
    const initMonth = selectedMonth;
    let initBw: Date | null = null;
    if (payPeriodType === "biweekly" && payPeriodAnchor) {
      initBw = bwPeriodStart ?? getBiweeklyPeriod(payPeriodAnchor, now).start;
    }
    setSessionModalYear(initYear);
    setSessionModalMonth(initMonth);
    setSessionModalBwStart(initBw);

    setSessionFilterFrom("");
    setSessionFilterTo("");
    setSessionHistoryStaff(member);
    setSessionHistory([]);
    setIsLoadingSessions(true);
    await fetchSessions(member.id, authToken, initYear, initMonth, initBw);
    setIsLoadingSessions(false);
  };

  // Apply custom date filter
  const applySessionDateFilter = async (from: string, to: string) => {
    if (!sessionHistoryStaff) return;
    const authToken = getAuthToken();
    if (!authToken) return;
    setIsLoadingSessions(true);
    await fetchSessions(sessionHistoryStaff.id, authToken, sessionModalYear, sessionModalMonth, sessionModalBwStart, from, to);
    setIsLoadingSessions(false);
  };

  // Clear custom date filter — revert to period view
  const clearSessionDateFilter = async () => {
    setSessionFilterFrom("");
    setSessionFilterTo("");
    if (!sessionHistoryStaff) return;
    const authToken = getAuthToken();
    if (!authToken) return;
    setIsLoadingSessions(true);
    await fetchSessions(sessionHistoryStaff.id, authToken, sessionModalYear, sessionModalMonth, sessionModalBwStart);
    setIsLoadingSessions(false);
  };

  // Navigate the session modal period and re-fetch
  const navigateSessionPeriod = async (direction: -1 | 1) => {
    if (!sessionHistoryStaff) return;
    const authToken = getAuthToken();
    if (!authToken) return;

    let newYear = sessionModalYear;
    let newMonth = sessionModalMonth;
    let newBw = sessionModalBwStart;

    if (payPeriodType === "biweekly" && sessionModalBwStart) {
      const d = new Date(sessionModalBwStart);
      d.setDate(d.getDate() + direction * 14);
      newBw = d;
      setSessionModalBwStart(d);
    } else {
      newMonth += direction;
      if (newMonth > 12) { newMonth = 1;  newYear++; }
      if (newMonth < 1)  { newMonth = 12; newYear--; }
      setSessionModalYear(newYear);
      setSessionModalMonth(newMonth);
    }

    setIsLoadingSessions(true);
    await fetchSessions(sessionHistoryStaff.id, authToken, newYear, newMonth, newBw);
    setIsLoadingSessions(false);
  };

  // Can we go forward in the session modal?
  const canSessionGoForward = (): boolean => {
    if (payPeriodType === "biweekly" && payPeriodAnchor && sessionModalBwStart) {
      const { start: curStart } = getBiweeklyPeriod(payPeriodAnchor, now);
      return sessionModalBwStart.getTime() < curStart.getTime();
    }
    return !(sessionModalYear === now.getFullYear() && sessionModalMonth === now.getMonth() + 1);
  };

  // Inline hours save
  const saveHours = async (staffId: string) => {
    const authToken = getAuthToken();
    if (!authToken || !studioId) return;
    const hours = hhmmToDecimal(editingHoursValue);
    if (isNaN(hours) || hours < 0) { setEditingHoursId(null); return; }
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/supabase/staff-hours`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({ staff_id: staffId, studio_id: studioId, year: selectedYear, month: selectedMonth, hours_logged: hours }),
      });
      const saved = res.ok ? await res.json() : null;
      setHoursMap((prev) => ({ ...prev, [staffId]: hours }));
      if (saved?.pay_earned != null) {
        setPayMap((prev) => ({ ...prev, [staffId]: saved.pay_earned }));
      }
    } catch (_) {}
    setEditingHoursId(null);
  };

  // Sign-in toggle — clock in or clock out, which auto-calculates hours
  const toggleSignIn = async (member: StaffMember) => {
    const authToken = getAuthToken();
    if (!authToken || !studioId) return;

    const clockingIn = !member.is_signed_in;
    const endpoint = clockingIn ? "clock-in" : "clock-out";
    const base = process.env.REACT_APP_BACKEND_URL + "/api/supabase";

    // Optimistic update
    setStaff((prev) => prev.map((m) => m.id === member.id ? { ...m, is_signed_in: clockingIn } : m));

    try {
      const res = await fetch(`${base}/staff/${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({ staff_id: member.id, studio_id: studioId }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || "Request failed");
      }

      // On clock-out, snap to the current period and refresh hours
      if (!clockingIn) {
        const today = new Date();
        let hoursUrl: string;

        if (payPeriodType === "biweekly" && payPeriodAnchor) {
          // Snap view to the period containing today
          const { start } = getBiweeklyPeriod(payPeriodAnchor, today);
          setBwPeriodStart(start);
          const { start: s, end: e } = getBiweeklyPeriod(payPeriodAnchor, today);
          hoursUrl = `${base}/staff-sessions/${studioId}?from=${toDateStr(s)}&to=${toDateStr(e)}`;
        } else {
          // Snap view to current month
          setSelectedYear(today.getFullYear());
          setSelectedMonth(today.getMonth() + 1);
          hoursUrl = `${base}/staff-hours/${studioId}?year=${today.getFullYear()}&month=${today.getMonth() + 1}`;
        }

        const hoursRes = await fetch(hoursUrl, { headers: { Authorization: `Bearer ${authToken}` } });
        if (hoursRes.ok) {
          const data: { staff_id: string; hours_logged: number; pay_earned: number }[] = await hoursRes.json();
          const hMap: Record<string, number> = {};
          const pMap: Record<string, number> = {};
          (Array.isArray(data) ? data : []).forEach((e) => {
            hMap[e.staff_id] = e.hours_logged;
            pMap[e.staff_id] = e.pay_earned ?? 0;
          });
          setHoursMap(hMap);
          setPayMap(pMap);
        }
      }
    } catch (err) {
      // Revert optimistic update
      setStaff((prev) => prev.map((m) => m.id === member.id ? { ...m, is_signed_in: member.is_signed_in } : m));
      setNotification({
        show: true,
        title: "Error",
        message: err instanceof Error ? err.message : "Failed to update sign-in status.",
      });
    }
  };

  // ── Shift scheduler helpers ──────────────────────────────────

  const shiftWeekDays = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(shiftWeekStart);
    d.setDate(d.getDate() + i);
    return d;
  });

  // Use local date parts — toISOString() is UTC and can shift the date for US timezones near midnight
  const toLocalDateStr = (d: Date) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  const fetchShifts = async (weekStart: Date) => {
    const authToken = getAuthToken();
    if (!authToken || !studioId) return;
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekEnd.getDate() + 6);
    const fromStr = toLocalDateStr(weekStart);
    const toStr = toLocalDateStr(weekEnd);
    setIsLoadingShifts(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/staff-shifts/${studioId}?from=${fromStr}&to=${toStr}`,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      if (res.ok) {
        const data = await res.json();
        setShifts(data);
      } else {
        const errText = await res.text();
        console.error('[fetchShifts] HTTP error', res.status, errText);
      }
    } catch (err) {
      console.error('[fetchShifts] Network error:', err);
    } finally {
      setIsLoadingShifts(false);
    }
  };

  const navigateShiftWeek = (dir: number) => {
    const next = new Date(shiftWeekStart);
    next.setDate(next.getDate() + dir * 7);
    setShiftWeekStart(next);
    fetchShifts(next);
  };

  const openShiftModal = (staffId: string, date: Date, existing?: any) => {
    const dateStr = toLocalDateStr(date);
    if (existing) {
      setEditingShift(existing);
      setShiftForm({
        staff_id: existing.staff_id,
        staff_role_id: existing.staff_role_id ?? "",
        shift_date: existing.shift_date,
        start_time: existing.start_time?.slice(0, 5) ?? "",
        end_time: existing.end_time?.slice(0, 5) ?? "",
        notes: existing.notes ?? "",
        is_recurring: existing.is_recurring ?? false,
        recurrence_days: existing.recurrence_days ?? [],
        recurrence_end_date: existing.recurrence_end_date ?? "",
      });
    } else {
      setEditingShift(null);
      setShiftForm({
        staff_id: staffId,
        staff_role_id: "",
        shift_date: dateStr,
        start_time: "",
        end_time: "",
        notes: "",
        is_recurring: false,
        recurrence_days: [],
        recurrence_end_date: "",
      });
    }
    setShowShiftModal(true);
  };

  const saveShift = async (addAnother = false) => {
    const authToken = getAuthToken();
    if (!authToken || !studioId) return;
    if (!shiftForm.staff_id || !shiftForm.shift_date || !shiftForm.start_time || !shiftForm.end_time) return;
    setIsSavingShift(true);
    try {
      const body = {
        ...shiftForm,
        studio_id: studioId,
        staff_role_id: shiftForm.staff_role_id || null,
        recurrence_end_date: shiftForm.recurrence_end_date || null,
      };
      const url = editingShift
        ? `${process.env.REACT_APP_BACKEND_URL}/api/supabase/staff-shifts/${editingShift.id}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/supabase/staff-shifts`;
      const res = await fetch(url, {
        method: editingShift ? "PUT" : "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        fetchShifts(shiftWeekStart);
        if (addAnother) {
          // Stay in the modal — clear times & role but keep staff, date, and recurrence settings
          setEditingShift(null);
          setShiftForm((f) => ({
            staff_id: f.staff_id,
            staff_role_id: "",
            shift_date: f.shift_date,
            start_time: "",
            end_time: "",
            notes: "",
            is_recurring: f.is_recurring,
            recurrence_days: f.recurrence_days,
            recurrence_end_date: f.recurrence_end_date,
          }));
        } else {
          setShowShiftModal(false);
        }
      }
    } catch { /* silent */ } finally {
      setIsSavingShift(false);
    }
  };

  const deleteShift = async (shiftId: string) => {
    const authToken = getAuthToken();
    if (!authToken) return;
    await fetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/supabase/staff-shifts/${shiftId}`,
      { method: "DELETE", headers: { Authorization: `Bearer ${authToken}` } }
    );
    setShowShiftModal(false);
    fetchShifts(shiftWeekStart);
  };

  // Export for a monthly period
  const exportToCSV = async (exportMonth: number, exportYear: number) => {
    const authToken = getAuthToken();
    if (!authToken || !studioId) return;

    setIsExporting(true);
    try {
      const base = process.env.REACT_APP_BACKEND_URL + "/api/supabase";
      const res = await fetch(
        `${base}/staff-hours/${studioId}?year=${exportYear}&month=${exportMonth}`,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      const hoursData: { staff_id: string; hours_logged: number; pay_earned: number }[] =
        res.ok ? await res.json() : [];

      const exportHoursMap: Record<string, number> = {};
      const exportPayMap: Record<string, number> = {};
      hoursData.forEach((e) => {
        exportHoursMap[e.staff_id] = e.hours_logged;
        exportPayMap[e.staff_id] = e.pay_earned ?? 0;
      });

      const monthLabel = `${MONTH_NAMES[exportMonth - 1]} ${exportYear}`;
      const headers = ["Staff #", "Name", "Role", "Email", "Phone", `Hours (${monthLabel})`, `Pay Earned (${monthLabel})`];

      const rows = staff.map((m) => {
        const hours = exportHoursMap[m.id] ?? 0;
        const pay = exportPayMap[m.id] ?? 0;
        return [
          m.staff_number ?? "",
          m.name,
          m.role_name?.name ?? "",
          m.email ?? "",
          m.phone ?? "",
          decimalToHHMMSS(hours),
          pay.toFixed(2),
        ];
      });

      const totalHours = staff.reduce((s, m) => s + (exportHoursMap[m.id] ?? 0), 0);
      const totalPay = staff.reduce((s, m) => s + (exportPayMap[m.id] ?? 0), 0);
      const totalsRow = ["", "TOTAL", "", "", "", decimalToHHMMSS(totalHours), totalPay.toFixed(2)];

      const csv = [headers, ...rows, totalsRow]
        .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","))
        .join("\n");

      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `staff-hours-${monthLabel.replace(" ", "-")}.csv`;
      link.click();
      URL.revokeObjectURL(url);
      setShowExportModal(false);
    } catch {
      setNotification({ show: true, title: "Error", message: "Failed to export data." });
    } finally {
      setIsExporting(false);
    }
  };

  // Export for a bi-weekly period
  const exportBwToCSV = async (periodStart: Date) => {
    const authToken = getAuthToken();
    if (!authToken || !studioId || !payPeriodAnchor) return;

    setIsExporting(true);
    try {
      const base = process.env.REACT_APP_BACKEND_URL + "/api/supabase";
      const { start, end } = getBiweeklyPeriod(payPeriodAnchor, periodStart);
      const from = toDateStr(start);
      const to = toDateStr(end);

      const res = await fetch(`${base}/staff-sessions/${studioId}?from=${from}&to=${to}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const sessionsData: { staff_id: string; hours_logged: number; pay_earned: number }[] =
        res.ok ? await res.json() : [];

      const exportHoursMap: Record<string, number> = {};
      const exportPayMap: Record<string, number> = {};
      sessionsData.forEach((e) => {
        exportHoursMap[e.staff_id] = e.hours_logged;
        exportPayMap[e.staff_id] = e.pay_earned ?? 0;
      });

      const periodLabel = formatPeriodLabel(start, end);
      const headers = ["Staff #", "Name", "Role", "Email", "Phone", `Hours (${periodLabel})`, `Pay Earned (${periodLabel})`];

      const rows = staff.map((m) => {
        const hours = exportHoursMap[m.id] ?? 0;
        const pay = exportPayMap[m.id] ?? 0;
        return [
          m.staff_number ?? "",
          m.name,
          m.role_name?.name ?? "",
          m.email ?? "",
          m.phone ?? "",
          decimalToHHMMSS(hours),
          pay.toFixed(2),
        ];
      });

      const totalHours = staff.reduce((s, m) => s + (exportHoursMap[m.id] ?? 0), 0);
      const totalPay = staff.reduce((s, m) => s + (exportPayMap[m.id] ?? 0), 0);
      const totalsRow = ["", "TOTAL", "", "", "", decimalToHHMMSS(totalHours), totalPay.toFixed(2)];

      const csv = [headers, ...rows, totalsRow]
        .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","))
        .join("\n");

      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `staff-hours-${from}-to-${to}.csv`;
      link.click();
      URL.revokeObjectURL(url);
      setShowExportModal(false);
    } catch {
      setNotification({ show: true, title: "Error", message: "Failed to export data." });
    } finally {
      setIsExporting(false);
    }
  };

  /** Generate the last N bi-weekly periods ending at or before today */
  const getRecentBwPeriods = (count: number): { start: Date; end: Date }[] => {
    if (!payPeriodAnchor) return [];
    const { start: currentStart } = getBiweeklyPeriod(payPeriodAnchor, now);
    const periods: { start: Date; end: Date }[] = [];
    for (let i = 0; i < count; i++) {
      const s = new Date(currentStart);
      s.setDate(s.getDate() - i * 14);
      const e = new Date(s);
      e.setDate(e.getDate() + 13);
      periods.push({ start: s, end: e });
    }
    return periods;
  };

  // Utility function for consistent input styling
  const getInputClassName = (baseClasses: string) => {
    return `${baseClasses} bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600`;
  };

  return (
    <div className="relative bg-gray-50 dark:bg-gray-900">
      <div className="space-y-4 p-0 pt-6 xl:pt-0 xl:p-6">
        <Notification
          show={notification.show}
          message={notification.message}
          title={notification.title}
          onClose={() => setNotification((prev) => ({ ...prev, show: false }))}
        />
        <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Staff Management
            </h1>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {canUseStaffPayroll
                ? "Manage your studio staff members and their information"
                : "Manage your staff roster and schedules. Upgrade for clock-in, hours, and payroll."}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {canUseStaffPayroll && (
            <button
              type="button"
              onClick={() => setShowExportModal(true)}
              className="inline-flex items-center rounded-md bg-white dark:bg-gray-700 px-3 py-2 text-sm font-semibold text-gray-700 dark:text-gray-200 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 w-fit xl:w-auto"
            >
              <ArrowDownTrayIcon className="-ml-0.5 mr-1.5 h-5 w-5 pointer-events-none" aria-hidden="true" />
              <span className="pointer-events-none">Export CSV</span>
            </button>
            )}
            <button
              type="button"
              onClick={() => {
                if (staffLimitReached) {
                  setNotification({
                    show: true,
                    message: `Your plan allows up to ${staffLimit} staff members. Upgrade to add more.`,
                    title: "Staff Limit Reached",
                  });
                  return;
                }
                setFormData({
                  name: "",
                  email: "",
                  phone: "",
                  roleAssignments: staffRoles.map((r) => ({ staff_role_id: r.id, hourly_rate: "", checked: false })),
                });
                setIsModalOpen(true);
              }}
              disabled={staffLimitReached}
              className={`inline-flex items-center rounded-md px-3 py-2 text-sm font-semibold shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 w-fit xl:w-auto ${
                staffLimitReached
                  ? "bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                  : "bg-indigo-600 text-white hover:bg-indigo-500 focus-visible:outline-indigo-600"
              }`}
            >
              <PlusIcon className="-ml-0.5 mr-1.5 h-5 w-5 pointer-events-none" aria-hidden="true" />
              <span className="pointer-events-none">
                Add Staff Member
                {staffLimit != null ? ` ${staff.length}/${staffLimit}` : ""}
              </span>
            </button>
          </div>
        </div>

        {/* Staff Dashboard — stat cards */}
        <div className={`grid grid-cols-1 gap-5 sm:grid-cols-2 mt-2 ${!canUseStaffPayroll ? "sm:grid-cols-1" : ""}`}>
          <StaffStatCard
            label="Total Instructors"
            value={staff.length}
            loading={loading}
            color="indigo"
            icon={UserGroupIcon}
            subtitle={
              staffLimit != null
                ? `${staff.length}/${staffLimit} staff on free plan`
                : "staff members on record"
            }
          />
          {canUseStaffPayroll && (
          <StaffStatCard
            label="Currently Signed In"
            value={staff.filter((m) => m.is_signed_in).length}
            loading={loading}
            color="green"
            icon={ArrowRightStartOnRectangleIcon}
            subtitle={`of ${staff.length} instructor${staff.length !== 1 ? "s" : ""}`}
          />
          )}
        </div>

        {!planLoading && !canUseStaffPayroll && (
          <UpgradePrompt capability="staffPayroll" collapsible collapseStorageKey="staffPayrollUpgrade" />
        )}

        {/* Add/Edit Staff Modal */}
        {isModalOpen && (
          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 backdrop-blur-sm transition-opacity" />
            <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
              <div className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                <div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
                  <button
                    type="button"
                    className="rounded-md bg-white dark:bg-gray-800 text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                    onClick={handleModalClose}
                  >
                    <span className="sr-only pointer-events-none">Close</span>
                    <XMarkIcon className="h-6 w-6 pointer-events-none" aria-hidden="true" />
                  </button>
                </div>
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-left w-full">
                    <h3 className="text-base font-semibold leading-6 text-gray-900 dark:text-white">
                      {editingStaff ? "Edit Staff Member" : "Add New Staff Member"}
                    </h3>
                    {editingStaff && editingStaff.staff_number != null && (
                      <div className="mt-2 flex justify-end">
                        <span className="px-4 py-1.5 bg-indigo-100 dark:bg-indigo-900 text-indigo-800 dark:text-indigo-200 rounded-full font-mono font-bold text-xl">
                          #{editingStaff.staff_number}
                        </span>
                      </div>
                    )}
                    <div className="mt-2">
                      <form className="space-y-4" onSubmit={handleSaveStaff}>
                        <div>
                          <label
                            htmlFor="name"
                            className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                          >
                            Name <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            name="name"
                            id="name"
                            required
                            maxLength={MAX_LENGTHS.name}
                            value={formData.name}
                            onChange={handleInputChange}
                            className={getInputClassName(
                              "mt-1 block w-full rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            )}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Roles &amp; Pay Rates
                          </label>
                          {isLoadingAssignments ? (
                            <div className="text-sm text-gray-500 dark:text-gray-400 py-2">Loading roles…</div>
                          ) : staffRoles.length === 0 ? (
                            <div className="text-sm text-gray-500 dark:text-gray-400 py-2">
                              No roles defined. Add roles in Settings → General Settings.
                            </div>
                          ) : (
                            <div className="space-y-2 rounded-lg border border-gray-200 dark:border-gray-600 p-3">
                              {formData.roleAssignments.map((assignment, idx) => {
                                const roleDef = staffRoles.find((r) => r.id === assignment.staff_role_id);
                                if (!roleDef) return null;
                                return (
                                  <div key={assignment.staff_role_id} className="flex items-center gap-3">
                                    <input
                                      type="checkbox"
                                      id={`role-${assignment.staff_role_id}`}
                                      checked={assignment.checked}
                                      onChange={(e) =>
                                        setFormData((prev) => ({
                                          ...prev,
                                          roleAssignments: prev.roleAssignments.map((a, i) =>
                                            i === idx ? { ...a, checked: e.target.checked } : a
                                          ),
                                        }))
                                      }
                                      className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500"
                                    />
                                    <label
                                      htmlFor={`role-${assignment.staff_role_id}`}
                                      className="flex-1 text-sm text-gray-700 dark:text-gray-300 cursor-pointer select-none"
                                    >
                                      {roleDef.name}
                                    </label>
                                    {assignment.checked && (
                                      <div className="flex items-center gap-1">
                                        <span className="text-xs text-gray-400 dark:text-gray-500">$</span>
                                        <input
                                          type="number"
                                          value={assignment.hourly_rate}
                                          onChange={(e) =>
                                            setFormData((prev) => ({
                                              ...prev,
                                              roleAssignments: prev.roleAssignments.map((a, i) =>
                                                i === idx ? { ...a, hourly_rate: e.target.value } : a
                                              ),
                                            }))
                                          }
                                          placeholder="0.00"
                                          min="0"
                                          step="0.01"
                                          className="w-20 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm px-2 py-1 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                                        />
                                        <span className="text-xs text-gray-400 dark:text-gray-500">/hr</span>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                        <div>
                          <label
                            htmlFor="email"
                            className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                          >
                            Email
                          </label>
                          <input
                            type="email"
                            name="email"
                            id="email"
                            maxLength={MAX_LENGTHS.email}
                            value={formData.email || ""}
                            onChange={handleInputChange}
                            className={getInputClassName(
                              "mt-1 block w-full rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            )}
                          />
                        </div>
                        <div>
                          <label
                            htmlFor="phone"
                            className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                          >
                            Phone
                          </label>
                          <input
                            type="tel"
                            name="phone"
                            id="phone"
                            maxLength={MAX_LENGTHS.phone}
                            value={formData.phone || ""}
                            onChange={handleInputChange}
                            className={getInputClassName(
                              "mt-1 block w-full rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            )}
                          />
                        </div>
                        <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                          <button
                            type="submit"
                            className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 sm:ml-3 sm:w-auto"
                          >
                            <span className="pointer-events-none">{editingStaff ? "Update" : "Add Staff Member"}</span>
                          </button>
                          <button
                            type="button"
                            className="mt-3 inline-flex w-full justify-center rounded-md bg-white dark:bg-gray-700 px-3 py-2 text-sm font-semibold text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 sm:mt-0 sm:w-auto"
                            onClick={handleModalClose}
                          >
                            <span className="pointer-events-none">Cancel</span>
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Search + period navigator */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex-1">
            <input
              type="text"
              maxLength={MAX_LENGTHS.searchTerm}
              className={getInputClassName(
                "block w-full rounded-md border-0 py-1.5 pl-3 pr-10 ring-1 ring-inset placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
              )}
              placeholder="Search staff..."
              value={searchTerm}
              onChange={handleSearchChange}
            />
          </div>

          <div className="flex items-center gap-2 shrink-0 flex-wrap">
            {canUseStaffPayroll && (
            <>
            {/* Period navigator */}
            <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">Hours for:</span>

            {payPeriodType === "biweekly" ? (
              activeBwPeriod ? (
                <>
                  <button
                    type="button"
                    onClick={goBwBack}
                    disabled={!canGoBwBack()}
                    className="p-1 rounded text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <ChevronLeftIcon className="h-4 w-4 pointer-events-none" />
                  </button>
                  <span className="min-w-[150px] text-center text-sm font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap">
                    {formatPeriodLabel(activeBwPeriod.start, activeBwPeriod.end)}
                  </span>
                  <button
                    type="button"
                    onClick={goBwForward}
                    disabled={!canGoBwForward()}
                    className="p-1 rounded text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <ChevronRightIcon className="h-4 w-4 pointer-events-none" />
                  </button>
                </>
              ) : (
                <span className="text-sm text-amber-600 dark:text-amber-400">
                  Set anchor date →
                </span>
              )
            ) : (
              <>
                <button
                  type="button"
                  onClick={goBack}
                  disabled={!canGoBack()}
                  className="p-1 rounded text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <ChevronLeftIcon className="h-4 w-4 pointer-events-none" />
                </button>
                <span className="min-w-[110px] text-center text-sm font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap">
                  {MONTH_NAMES[selectedMonth - 1]} {selectedYear}
                </span>
                <button
                  type="button"
                  onClick={goForward}
                  disabled={!canGoForward()}
                  className="p-1 rounded text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <ChevronRightIcon className="h-4 w-4 pointer-events-none" />
                </button>
              </>
            )}

            {/* Pay period settings gear */}
            <button
              type="button"
              onClick={() => {
                setSettingsType(payPeriodType);
                setSettingsAnchor(payPeriodAnchor ?? "");
                setShowPayPeriodSettings(true);
              }}
              title="Pay period settings"
              className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <Cog6ToothIcon className="h-5 w-5" />
            </button>
            </>
            )}
          </div>
        </div>

        {/* Loading Message */}
        {loading && (
          <div className="flex justify-center items-center py-8">
            <div className="animate-pulse flex space-x-4 items-center">
              <div className="h-5 w-5 bg-indigo-200 dark:bg-indigo-600 rounded-full animate-spin"></div>
              <div className="text-sm text-gray-600 dark:text-gray-400">
                Loading staff information...
              </div>
            </div>
          </div>
        )}

        {/* Table */}
        {!loading && (
          <div className="mt-4 relative">
            <div className="overflow-x-auto">
              <div className="inline-block min-w-full py-2 align-middle">
                <div className="relative overflow-hidden bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                  <table className="min-w-full divide-y divide-gray-300 dark:divide-gray-600">
                    <thead>
                      <tr>
                        <th scope="col" className="py-3.5 pr-4 text-left text-sm font-bold font-sans text-gray-900 dark:text-white whitespace-nowrap">
                          Name
                        </th>
                        <th scope="col" className="py-3.5 pr-4 text-left text-sm font-bold font-sans text-gray-900 dark:text-white whitespace-nowrap">
                          Contact
                        </th>
                        {canUseStaffPayroll && (
                        <>
                        <th scope="col" className="py-3.5 pr-4 text-left text-sm font-bold font-sans text-gray-900 dark:text-white whitespace-nowrap">
                          Signed In
                        </th>
                        <th scope="col" className="py-3.5 pr-4 text-left text-sm font-bold font-sans text-gray-900 dark:text-white whitespace-nowrap">
                          Time Worked
                        </th>
                        <th scope="col" className="py-3.5 pr-4 text-left text-sm font-bold font-sans text-gray-900 dark:text-white whitespace-nowrap">
                          Total Pay
                        </th>
                        </>
                        )}
                        <th scope="col" className="py-3.5 pr-4 text-left text-sm font-bold font-sans text-gray-900 dark:text-white">
                          Classes
                        </th>
                        <th scope="col" className="py-3.5 text-left text-sm font-bold font-sans text-gray-900 dark:text-white">
                          <span className="sr-only">Actions</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
                      {filteredStaff.map((member) => {
                        const hours = hoursMap[member.id] ?? 0;
                        const total = payMap[member.id] ?? 0;
                        const classes = classesForStaff[member.id] ?? [];
                        const isEditingHours = editingHoursId === member.id;

                        return (
                          <tr key={member.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/40 transition">
                            {/* Name */}
                            <td className="whitespace-nowrap py-4 pr-4 text-sm font-medium text-gray-900 dark:text-white">
                              <div className="flex items-center gap-2">
                                <UserIcon className="h-5 w-5 text-gray-400 dark:text-gray-500 shrink-0" />
                                <span>{member.name}</span>
                                {member.staff_number != null && (
                                  <span className="inline-flex items-center rounded-full bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 text-xs px-2 py-0.5 font-mono">
                                    #{member.staff_number}
                                  </span>
                                )}
                              </div>
                            </td>

                            {/* Contact */}
                            <td className="py-4 pr-4 text-sm text-gray-500 dark:text-gray-400">
                              <div className="flex items-center gap-1.5">
                                <EnvelopeIcon className="h-4 w-4 text-gray-400 dark:text-gray-500 shrink-0" />
                                <span className="whitespace-nowrap">{member.email || ""}</span>
                              </div>
                              <div className="flex items-center gap-1.5 mt-0.5">
                                <PhoneIcon className="h-4 w-4 text-gray-400 dark:text-gray-500 shrink-0" />
                                <span className="whitespace-nowrap">{member.phone || ""}</span>
                              </div>
                            </td>

                            {canUseStaffPayroll && (
                            <>
                            {/* Sign-in indicator (read-only) */}
                            <td className="whitespace-nowrap py-4 pr-4">
                              <div
                                className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                                  member.is_signed_in
                                    ? "bg-green-600 border-green-600 dark:bg-green-500 dark:border-green-500"
                                    : "border-gray-300 dark:border-gray-600"
                                }`}
                              >
                                {member.is_signed_in && (
                                  <CheckIcon className="h-3 w-3 text-white" />
                                )}
                              </div>
                            </td>

                            {/* Time Worked — read-only */}
                            <td className="whitespace-nowrap py-4 pr-4 text-sm">
                              <span className="text-gray-700 dark:text-gray-300">{formatHoursWithSeconds(hours)}</span>
                            </td>

                            {/* Total Pay + session history trigger */}
                            <td className="whitespace-nowrap py-4 pr-4 text-sm">
                              <div className="flex items-center gap-1.5">
                                <span className="font-semibold text-indigo-700 dark:text-indigo-400">${total.toFixed(2)}</span>
                                <button
                                  type="button"
                                  title="View clock-in/out history"
                                  onClick={() => openSessionHistory(member)}
                                  className="text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 transition"
                                >
                                  <ClockIcon className="h-3.5 w-3.5 pointer-events-none" />
                                </button>
                              </div>
                            </td>
                            </>
                            )}

                            {/* Assigned classes */}
                            <td className="py-4 pr-4 text-sm">
                              {classes.length === 0 ? (
                                <span className="text-gray-400 dark:text-gray-500 text-xs italic">None</span>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => setClassesModalStaff(member)}
                                  className="inline-flex items-center gap-1.5 rounded-full bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 text-xs px-3 py-1 font-medium hover:bg-indigo-200 dark:hover:bg-indigo-800/60 transition whitespace-nowrap"
                                >
                                  <AcademicCapIcon className="h-3.5 w-3.5 pointer-events-none" />
                                  <span className="pointer-events-none">
                                    {classes.length} {classes.length === 1 ? "Class" : "Classes"}
                                  </span>
                                </button>
                              )}
                            </td>

                            {/* Actions */}
                            <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                              <button
                                type="button"
                                className="bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 dark:hover:text-indigo-300 hover:bg-gray-100 dark:hover:bg-gray-600 rounded p-2 mr-2"
                                onClick={() => handleEditStaff(member)}
                              >
                                <PencilIcon className="h-5 w-5 pointer-events-none" />
                              </button>
                              <button
                                type="button"
                                className="bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 hover:bg-gray-100 dark:hover:bg-gray-600 rounded p-2"
                                onClick={() => setStaffToDelete(member)}
                              >
                                <TrashIcon className="h-5 w-5 pointer-events-none" />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>

                    {/* Totals footer */}
                    {canUseStaffPayroll && filteredStaff.length > 0 && (
                      <tfoot>
                        <tr className="border-t-2 border-gray-200 dark:border-gray-600">
                          <td colSpan={3} className="py-3 pr-4 text-sm font-bold text-gray-700 dark:text-gray-300">
                            Totals
                          </td>
                          <td className="py-3 pr-4 text-sm font-bold text-gray-900 dark:text-white whitespace-nowrap">
                            {formatHoursWithSeconds(filteredStaff.reduce((s, m) => s + (hoursMap[m.id] ?? 0), 0))}
                          </td>
                          <td className="py-3 pr-4 text-sm font-bold text-indigo-700 dark:text-indigo-400 whitespace-nowrap">
                            ${filteredStaff.reduce((s, m) => s + (payMap[m.id] ?? 0), 0).toFixed(2)}
                          </td>
                          <td colSpan={2} />
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── Shift Scheduler ────────────────────────────────── */}
        {!loading && (
          <div className="mt-6">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              {/* Header */}
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Staff Schedules</h2>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => navigateShiftWeek(-1)}
                    className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400"
                  >
                    <ChevronLeftIcon className="h-4 w-4" />
                  </button>
                  <span className="text-xs font-medium text-gray-700 dark:text-gray-300 min-w-[130px] text-center">
                    {shiftWeekDays[0].toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                    {" – "}
                    {shiftWeekDays[6].toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                  </span>
                  <button
                    type="button"
                    onClick={() => navigateShiftWeek(1)}
                    className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400"
                  >
                    <ChevronRightIcon className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const d = new Date();
                      const day = d.getDay();
                      const diff = day === 0 ? -6 : 1 - day;
                      d.setDate(d.getDate() + diff);
                      d.setHours(0, 0, 0, 0);
                      setShiftWeekStart(d);
                      fetchShifts(d);
                    }}
                    className="px-2 py-1 text-xs rounded border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    Today
                  </button>
                </div>
              </div>

              {isLoadingShifts ? (
                <div className="flex justify-center py-8">
                  <div className="h-5 w-5 animate-spin rounded-full border-2 border-indigo-500 border-t-transparent" />
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full border-collapse">
                    <thead>
                      <tr>
                        <th className="w-36 py-2 pr-4 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                          Staff
                        </th>
                        {shiftWeekDays.map((day) => {
                          const isToday = toLocalDateStr(day) === toLocalDateStr(new Date());
                          return (
                            <th key={day.toISOString()} className="py-2 px-2 text-center min-w-[110px]">
                              <div className={`text-xs font-semibold uppercase tracking-wide ${isToday ? "text-indigo-600 dark:text-indigo-400" : "text-gray-500 dark:text-gray-400"}`}>
                                {day.toLocaleDateString("en-US", { weekday: "short" })}
                              </div>
                              <div className={`text-sm font-bold mt-0.5 ${isToday ? "text-indigo-600 dark:text-indigo-400" : "text-gray-900 dark:text-white"}`}>
                                {day.getDate()}
                              </div>
                            </th>
                          );
                        })}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                      {staff.map((member) => {
                        const weekTotalHours = shiftWeekDays.reduce((sum, day) => {
                          const dateStr = toLocalDateStr(day);
                          return sum + shifts
                            .filter((s) => s.staff_id === member.id && s.effective_date === dateStr)
                            .reduce((daySum: number, s: any) => {
                              const [sh, sm] = s.start_time.split(":").map(Number);
                              const [eh, em] = s.end_time.split(":").map(Number);
                              return daySum + (eh * 60 + em - (sh * 60 + sm)) / 60;
                            }, 0);
                        }, 0);
                        const wh = Math.floor(weekTotalHours);
                        const wm = Math.round((weekTotalHours - wh) * 60);
                        const weekLabel = wh === 0 && wm === 0 ? null : wm === 0 ? `${wh}h` : `${wh}h ${wm}m`;
                        return (
                        <tr key={member.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                          <td className="py-3 pr-4 text-sm font-medium text-gray-900 dark:text-white whitespace-nowrap">
                            {member.name}
                            {member.staff_number != null && (
                              <span className="ml-1.5 text-xs text-gray-400 dark:text-gray-500 font-mono">#{member.staff_number}</span>
                            )}
                            {weekLabel && (
                              <div className="text-xs text-gray-400 dark:text-gray-500 font-normal mt-0.5">{weekLabel} this week</div>
                            )}
                          </td>
                          {shiftWeekDays.map((day) => {
                            const dateStr = toLocalDateStr(day);
                            const dayShifts = shifts
                              .filter((s) => s.staff_id === member.id && s.effective_date === dateStr)
                              .sort((a: any, b: any) => a.start_time.localeCompare(b.start_time));
                            const fmtT = (t: string) => {
                              const [h, m] = t.split(":").map(Number);
                              const ampm = h >= 12 ? "pm" : "am";
                              const h12 = h % 12 || 12;
                              return m === 0 ? `${h12}${ampm}` : `${h12}:${String(m).padStart(2,"0")}${ampm}`;
                            };
                            const totalScheduledHours = dayShifts.reduce((sum: number, s: any) => {
                              const [sh, sm] = s.start_time.split(":").map(Number);
                              const [eh, em] = s.end_time.split(":").map(Number);
                              return sum + (eh * 60 + em - (sh * 60 + sm)) / 60;
                            }, 0);
                            const fmtScheduled = (hrs: number) => {
                              const h = Math.floor(hrs);
                              const m = Math.round((hrs - h) * 60);
                              return m === 0 ? `${h}h` : `${h}h ${m}m`;
                            };
                            return (
                              <td key={dateStr} className="py-2 px-2 align-top">
                                <div className="flex flex-col gap-1">
                                  {dayShifts.length > 0 && (
                                    <div className="text-xs text-gray-400 dark:text-gray-500 font-medium mb-0.5">
                                      {fmtScheduled(totalScheduledHours)} scheduled
                                    </div>
                                  )}
                                  {dayShifts.map((dayShift: any) => (
                                    <button
                                      key={dayShift.id}
                                      type="button"
                                      onClick={() => openShiftModal(member.id, day, dayShift)}
                                      className="w-full group rounded-lg bg-indigo-50 dark:bg-indigo-900/30 border border-indigo-200 dark:border-indigo-700 px-2 py-1.5 text-left hover:bg-indigo-100 dark:hover:bg-indigo-900/50 transition"
                                    >
                                      <div className="text-xs font-semibold text-indigo-700 dark:text-indigo-300 truncate">
                                        {fmtT(dayShift.start_time)}–{fmtT(dayShift.end_time)}
                                      </div>
                                      {dayShift.staff_roles?.name && (
                                        <div className="text-xs text-indigo-500 dark:text-indigo-400 truncate mt-0.5">
                                          {dayShift.staff_roles.name}
                                        </div>
                                      )}
                                      {dayShift.is_recurring && (
                                        <div className="text-xs text-indigo-400 dark:text-indigo-500 mt-0.5">↻</div>
                                      )}
                                    </button>
                                  ))}
                                  {/* Always-visible add button — full dashed when empty, compact when shifts exist */}
                                  {dayShifts.length === 0 ? (
                                    <button
                                      type="button"
                                      onClick={() => openShiftModal(member.id, day)}
                                      className="w-full h-10 rounded-lg border border-dashed border-gray-200 dark:border-gray-600 text-gray-300 dark:text-gray-600 hover:border-indigo-300 dark:hover:border-indigo-600 hover:text-indigo-400 dark:hover:text-indigo-500 transition text-lg"
                                    >
                                      +
                                    </button>
                                  ) : (
                                    <button
                                      type="button"
                                      onClick={() => openShiftModal(member.id, day)}
                                      className="w-full rounded border border-dashed border-gray-200 dark:border-gray-700 text-gray-300 dark:text-gray-600 hover:border-indigo-300 dark:hover:border-indigo-600 hover:text-indigo-400 dark:hover:text-indigo-500 transition text-xs py-0.5"
                                      title="Add another shift"
                                    >
                                      + add
                                    </button>
                                  )}
                                </div>
                              </td>
                            );
                          })}
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Classes Modal */}
        {classesModalStaff && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-lg max-h-[80vh] flex flex-col">
              <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-gray-200 dark:border-gray-700">
                <div>
                  <h3 className="text-base font-semibold text-gray-900 dark:text-white">Assigned Classes</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{classesModalStaff.name}</p>
                </div>
                <button type="button" onClick={() => setClassesModalStaff(null)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
              <div className="overflow-auto flex-1 px-6 py-4 space-y-3">
                {(classesForStaff[classesModalStaff.id] ?? []).map((cls) => (
                  <div key={cls.id} className="flex items-start justify-between rounded-lg border border-gray-200 dark:border-gray-700 px-4 py-3">
                    <div>
                      <div className="text-sm font-medium text-gray-900 dark:text-white">{cls.class_name}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        {cls.days.join(", ")}
                        {cls.start_time &&
                          ` · ${formatScheduleTime(cls.start_time)}–${formatScheduleTime(cls.end_time)}`}
                        {cls.age_range && ` · ${cls.age_range}`}
                      </div>
                    </div>
                    {cls.type && (
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${cls.type === "Private" ? "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300" : "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300"}`}>
                        {cls.type}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Session History Modal */}
        {sessionHistoryStaff && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
              {/* Header */}
              <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-gray-200 dark:border-gray-700">
                <div>
                  <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                    Clock-in / Clock-out History
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{sessionHistoryStaff.name}</p>
                </div>
                <button type="button" onClick={() => setSessionHistoryStaff(null)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              {/* Period navigation */}
              <div className="px-6 py-3 border-b border-gray-100 dark:border-gray-700 flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => navigateSessionPeriod(-1)}
                    className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500"
                  >
                    <ChevronLeftIcon className="h-4 w-4" />
                  </button>
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300 min-w-[120px] text-center">
                    {payPeriodType === "biweekly" && sessionModalBwStart
                      ? (() => { const { start, end } = getBiweeklyPeriod(payPeriodAnchor!, sessionModalBwStart); const fmt = (d: Date) => `${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}-${d.getFullYear()}`; return `${fmt(start)} – ${fmt(end)}`; })()
                      : new Date(sessionModalYear, sessionModalMonth - 1).toLocaleDateString("en-US", { month: "long", year: "numeric" })
                    }
                  </span>
                  <button
                    type="button"
                    onClick={() => navigateSessionPeriod(1)}
                    disabled={!canSessionGoForward()}
                    className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 disabled:opacity-30"
                  >
                    <ChevronRightIcon className="h-4 w-4" />
                  </button>
                </div>

                {/* Custom date filter */}
                <div className="flex items-center gap-2 ml-auto flex-wrap">
                  <input
                    type="date"
                    value={sessionFilterFrom}
                    onChange={(e) => setSessionFilterFrom(e.target.value)}
                    className="text-xs rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 px-2 py-1"
                  />
                  <span className="text-xs text-gray-400">to</span>
                  <input
                    type="date"
                    value={sessionFilterTo}
                    onChange={(e) => setSessionFilterTo(e.target.value)}
                    className="text-xs rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 px-2 py-1"
                  />
                  <button
                    type="button"
                    onClick={() => applySessionDateFilter(sessionFilterFrom, sessionFilterTo)}
                    disabled={!sessionFilterFrom || !sessionFilterTo}
                    className="text-xs px-3 py-1 rounded bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40"
                  >
                    Filter
                  </button>
                  {(sessionFilterFrom || sessionFilterTo) && (
                    <button
                      type="button"
                      onClick={clearSessionDateFilter}
                      className="text-xs px-2 py-1 rounded border border-gray-300 dark:border-gray-600 text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700"
                    >
                      Clear
                    </button>
                  )}
                </div>
              </div>

              {/* Body */}
              <div className="overflow-auto flex-1 px-6 py-4">
                {isLoadingSessions ? (
                  <div className="flex justify-center py-10">
                    <div className="h-5 w-5 animate-spin rounded-full border-2 border-indigo-500 border-t-transparent" />
                  </div>
                ) : sessionHistory.length === 0 ? (
                  <p className="text-center text-sm text-gray-400 dark:text-gray-500 py-10">No sessions found for this period.</p>
                ) : (
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200 dark:border-gray-700">
                        <th className="py-2 pr-4 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Clock In</th>
                        <th className="py-2 pr-4 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Clock Out</th>
                        <th className="py-2 pr-4 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Time Worked</th>
                        <th className="py-2 pr-4 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Pay</th>
                        <th className="py-2 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Role</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                      {sessionHistory.map((s: any) => {
                        const fmtTs = (ts: string | null) => {
                          if (!ts) return <span className="text-gray-400 italic">—</span>;
                          const d = new Date(ts);
                          return d.toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit", second: "2-digit" });
                        };
                        const tsToTimeStr = (ts: string | null) => {
                          if (!ts) return "";
                          const d = new Date(ts);
                          return `${String(d.getHours()).padStart(2,"0")}:${String(d.getMinutes()).padStart(2,"0")}:${String(d.getSeconds()).padStart(2,"0")}`;
                        };
                        const isEditingIn  = editingSession?.id === s.id && editingSession?.field === "in";
                        const isEditingOut = editingSession?.id === s.id && editingSession?.field === "out";
                        const roleName  = s.staff_roles?.name ?? null;
                        return (
                          <tr key={s.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                            <td className="py-2 pr-4 whitespace-nowrap">
                              {isEditingIn ? (
                                <input
                                  type="time"
                                  step="1"
                                  autoFocus
                                  value={editingSession!.value}
                                  onChange={(e) => setEditingSession((prev) => prev ? { ...prev, value: e.target.value } : null)}
                                  onBlur={(e) => saveSessionEdit(e.target.value)}
                                  onKeyDown={(e) => { if (e.key === "Enter") saveSessionEdit((e.target as HTMLInputElement).value); if (e.key === "Escape") setEditingSession(null); }}
                                  className="text-sm rounded border border-indigo-400 px-2 py-1 w-44"
                                />
                              ) : (
                                <button
                                  type="button"
                                  className="text-gray-700 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-indigo-400 text-left"
                                  onClick={() => setEditingSession({ id: s.id, field: "in", value: tsToTimeStr(s.clocked_in_at) })}
                                >
                                  {fmtTs(s.clocked_in_at)}
                                </button>
                              )}
                            </td>
                            <td className="py-2 pr-4 whitespace-nowrap">
                              {isEditingOut ? (
                                <input
                                  type="time"
                                  step="1"
                                  autoFocus
                                  value={editingSession!.value}
                                  onChange={(e) => setEditingSession((prev) => prev ? { ...prev, value: e.target.value } : null)}
                                  onBlur={(e) => saveSessionEdit(e.target.value)}
                                  onKeyDown={(e) => { if (e.key === "Enter") saveSessionEdit((e.target as HTMLInputElement).value); if (e.key === "Escape") setEditingSession(null); }}
                                  className="text-sm rounded border border-indigo-400 px-2 py-1 w-44"
                                />
                              ) : (
                                <button
                                  type="button"
                                  className="text-gray-700 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-indigo-400 text-left"
                                  onClick={() => s.clocked_out_at && setEditingSession({ id: s.id, field: "out", value: tsToTimeStr(s.clocked_out_at) })}
                                >
                                  {fmtTs(s.clocked_out_at)}
                                </button>
                              )}
                            </td>
                            <td className="py-2 pr-4 text-gray-600 dark:text-gray-400 whitespace-nowrap">
                              {s.hours_worked != null ? formatHoursWithSeconds(s.hours_worked) : "—"}
                            </td>
                            <td className="py-2 pr-4 text-gray-600 dark:text-gray-400 whitespace-nowrap">
                              {s.pay_earned != null ? `$${Number(s.pay_earned).toFixed(2)}` : "—"}
                            </td>
                            <td className="py-2 text-gray-600 dark:text-gray-400 whitespace-nowrap">
                              {roleName ?? <span className="text-gray-400 italic text-xs">—</span>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr className="border-t-2 border-gray-200 dark:border-gray-600">
                        <td colSpan={2} className="py-2 pr-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Totals</td>
                        <td className="py-2 pr-4 text-sm font-semibold text-gray-800 dark:text-gray-200">
                          {formatHoursWithSeconds(sessionHistory.reduce((a, s) => a + (s.hours_worked ?? 0), 0))}
                        </td>
                        <td className="py-2 pr-4 text-sm font-semibold text-indigo-700 dark:text-indigo-400">
                          ${sessionHistory.reduce((a, s) => a + Number(s.pay_earned ?? 0), 0).toFixed(2)}
                        </td>
                        <td />
                      </tr>
                    </tfoot>
                  </table>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Shift Modal */}
        {showShiftModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md">
              <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                  {editingShift ? "Edit Shift" : "Add Shift"}
                </h3>
                <button type="button" onClick={() => setShowShiftModal(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
              <div className="px-6 py-5 space-y-4">
                {/* Staff (read-only if pre-filled) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Staff Member</label>
                  <select
                    value={shiftForm.staff_id}
                    onChange={(e) => setShiftForm((f) => ({ ...f, staff_id: e.target.value }))}
                    className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="">Select staff…</option>
                    {staff.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                  </select>
                </div>

                {/* Role */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Role / Rate</label>
                  <select
                    value={shiftForm.staff_role_id}
                    onChange={(e) => setShiftForm((f) => ({ ...f, staff_role_id: e.target.value }))}
                    className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="">No specific role</option>
                    {staffRoles.map((r: any) => <option key={r.id} value={r.id}>{r.name}</option>)}
                  </select>
                </div>

                {/* Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {shiftForm.is_recurring ? "Starts From" : "Date"}
                  </label>
                  <input
                    type="date"
                    value={shiftForm.shift_date}
                    onChange={(e) => setShiftForm((f) => ({ ...f, shift_date: e.target.value }))}
                    className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                  {shiftForm.is_recurring && (
                    <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">Recurring pattern won't apply before this date.</p>
                  )}
                </div>

                {/* Times */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Start Time</label>
                    <input
                      type="time"
                      value={shiftForm.start_time}
                      onChange={(e) => setShiftForm((f) => ({ ...f, start_time: e.target.value }))}
                      className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">End Time</label>
                    <input
                      type="time"
                      value={shiftForm.end_time}
                      onChange={(e) => setShiftForm((f) => ({ ...f, end_time: e.target.value }))}
                      className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                </div>

                {/* Recurring */}
                <div className="flex items-center gap-2">
                  <input
                    id="recurring"
                    type="checkbox"
                    checked={shiftForm.is_recurring}
                    onChange={(e) => setShiftForm((f) => ({ ...f, is_recurring: e.target.checked }))}
                    className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <label htmlFor="recurring" className="text-sm font-medium text-gray-700 dark:text-gray-300">Recurring shift</label>
                </div>

                {shiftForm.is_recurring && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Repeat on <span className="text-red-500">*</span>
                      </label>
                      <div className="flex gap-1.5 flex-wrap">
                        {["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"].map((day) => {
                          const active = shiftForm.recurrence_days.includes(day);
                          return (
                            <button
                              key={day}
                              type="button"
                              onClick={() => setShiftForm((f) => ({
                                ...f,
                                recurrence_days: active
                                  ? f.recurrence_days.filter((d) => d !== day)
                                  : [...f.recurrence_days, day],
                              }))}
                              className={`px-2.5 py-1 rounded-full text-xs font-medium border transition ${
                                active
                                  ? "bg-indigo-600 border-indigo-600 text-white"
                                  : "border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-indigo-400"
                              }`}
                            >
                              {day.slice(0, 3)}
                            </button>
                          );
                        })}
                      </div>
                      {shiftForm.recurrence_days.length === 0 && (
                        <p className="mt-1.5 text-xs text-red-500 dark:text-red-400">
                          Select at least one day to repeat on.
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        End Date <span className="text-gray-400 font-normal">(leave blank for indefinite)</span>
                      </label>
                      <input
                        type="date"
                        value={shiftForm.recurrence_end_date}
                        onChange={(e) => setShiftForm((f) => ({ ...f, recurrence_end_date: e.target.value }))}
                        className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>
                  </>
                )}

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Notes <span className="text-gray-400 font-normal">(optional)</span></label>
                  <input
                    type="text"
                    value={shiftForm.notes}
                    onChange={(e) => setShiftForm((f) => ({ ...f, notes: e.target.value }))}
                    placeholder="e.g. Front desk coverage"
                    className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>

              <div className="flex items-center justify-between px-6 pb-5">
                {editingShift ? (
                  <button
                    type="button"
                    onClick={() => deleteShift(editingShift.id)}
                    className="text-sm text-red-600 dark:text-red-400 hover:underline"
                  >
                    Delete shift
                  </button>
                ) : <div />}
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setShowShiftModal(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                  {/* "Save & Add Another" only shown when creating a new shift */}
                  {!editingShift && (
                    <button
                      type="button"
                      disabled={
                        isSavingShift ||
                        !shiftForm.staff_id ||
                        !shiftForm.shift_date ||
                        !shiftForm.start_time ||
                        !shiftForm.end_time ||
                        (shiftForm.is_recurring && shiftForm.recurrence_days.length === 0)
                      }
                      onClick={() => saveShift(true)}
                      className="px-4 py-2 text-sm font-medium text-indigo-700 dark:text-indigo-300 bg-indigo-50 dark:bg-indigo-900/30 border border-indigo-300 dark:border-indigo-600 rounded-md hover:bg-indigo-100 dark:hover:bg-indigo-900/50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSavingShift ? "Saving…" : "Save & Add Another"}
                    </button>
                  )}
                  <button
                    type="button"
                    disabled={
                      isSavingShift ||
                      !shiftForm.staff_id ||
                      !shiftForm.shift_date ||
                      !shiftForm.start_time ||
                      !shiftForm.end_time ||
                      (shiftForm.is_recurring && shiftForm.recurrence_days.length === 0)
                    }
                    onClick={() => saveShift(false)}
                    className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSavingShift ? "Saving…" : editingShift ? "Update" : "Add Shift"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Delete Staff Confirmation Modal */}
      {staffToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md p-6">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 flex items-center justify-center h-10 w-10 rounded-full bg-red-100 dark:bg-red-900/30">
                <TrashIcon className="h-5 w-5 text-red-600 dark:text-red-400" />
              </div>
              <div className="flex-1">
                <h3 className="text-base font-semibold text-gray-900 dark:text-white">Delete Staff Member</h3>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  Are you sure you want to delete <span className="font-medium text-gray-900 dark:text-white">{staffToDelete.name}</span>? This action cannot be undone and will remove all their data.
                </p>
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-3">
              <button
                type="button"
                onClick={cancelDelete}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDelete}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Export CSV Modal */}
      {showExportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-sm p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Export Staff Hours</h2>
              <button
                type="button"
                onClick={() => setShowExportModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            {payPeriodType === "monthly" ? (
              <div className="space-y-3">
                <p className="text-sm text-gray-600 dark:text-gray-400">Select the month to export:</p>
                <div className="flex items-center gap-2">
                  <select
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(Number(e.target.value))}
                    className="flex-1 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm"
                  >
                    {MONTH_NAMES.map((name, i) => (
                      <option key={i} value={i + 1}>{name}</option>
                    ))}
                  </select>
                  <select
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(Number(e.target.value))}
                    className="w-28 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm"
                  >
                    {[now.getFullYear() - 1, now.getFullYear()].map((y) => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                </div>
                <button
                  type="button"
                  disabled={isExporting}
                  onClick={() => exportToCSV(selectedMonth, selectedYear)}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-500 disabled:opacity-50"
                >
                  <ArrowDownTrayIcon className="h-4 w-4" />
                  {isExporting ? "Exporting..." : `Export ${MONTH_NAMES[selectedMonth - 1]} ${selectedYear}`}
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-gray-600 dark:text-gray-400">Select a pay period to export:</p>
                <div className="space-y-2 max-h-52 overflow-y-auto pr-1">
                  {getRecentBwPeriods(6).map(({ start, end }) => {
                    const label = formatPeriodLabel(start, end);
                    return (
                      <button
                        key={start.toISOString()}
                        type="button"
                        disabled={isExporting}
                        onClick={() => exportBwToCSV(start)}
                        className="w-full text-left rounded-md border border-gray-200 dark:border-gray-600 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 hover:border-indigo-300 dark:hover:border-indigo-600 transition disabled:opacity-50"
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      {/* Pay Period Settings Modal */}
      {showPayPeriodSettings && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-sm">
            <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-base font-semibold text-gray-900 dark:text-white">Pay Period Settings</h3>
              <button
                type="button"
                onClick={() => setShowPayPeriodSettings(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="px-6 py-5 space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Pay Period Type</label>
                <div className="flex gap-3">
                  {(["monthly", "biweekly"] as const).map((mode) => (
                    <button
                      key={mode}
                      type="button"
                      onClick={() => setSettingsType(mode)}
                      className={`flex-1 py-2 rounded-md text-sm font-medium border transition ${
                        settingsType === mode
                          ? "bg-indigo-600 border-indigo-600 text-white"
                          : "border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-indigo-400"
                      }`}
                    >
                      {mode.charAt(0).toUpperCase() + mode.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
              {settingsType === "biweekly" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Anchor Date <span className="text-xs text-gray-400 font-normal">(first day of a pay period)</span>
                  </label>
                  <input
                    type="date"
                    value={settingsAnchor}
                    onChange={(e) => setSettingsAnchor(e.target.value)}
                    className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                  <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
                    Biweekly periods are calculated forward from this date.
                  </p>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 px-6 pb-5">
              <button
                type="button"
                onClick={() => setShowPayPeriodSettings(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={settingsType === "biweekly" && !settingsAnchor}
                onClick={savePayPeriodSettings}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
